import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  Chip
} from '@mui/material';
import { PlayCircleOutline as PlayIcon, VideoLibrary as VideoIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getAllTutorials } from '../config/tutorials.config';
import TutorialPrompt from '../components/shared/tutorial/TutorialPrompt';
import TutorialVideoPlayer from '../components/shared/tutorial/TutorialVideoPlayer';
import { markTutorialWatched } from '../utilis/tutorial-storage';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: theme.palette.primary.main,
  },
}));

const PlayButtonContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 3,
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  pointerEvents: 'none',
  '& .MuiIconButton-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    color: theme.palette.primary.main,
    pointerEvents: 'auto',
    boxShadow: theme.shadows[8],
    width: 64,
    height: 64,
    minWidth: 64,
    minHeight: 64,
    borderRadius: '50%',
    padding: theme.spacing(1.25), // 10px padding for proper centering
    '& svg': {
      fontSize: 40,
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      transform: 'scale(1.1)',
    },
    transition: 'all 0.3s ease-in-out',
  },
}));

const CardImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 180,
  backgroundColor: '#000',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.4) 100%)',
    zIndex: 1
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    zIndex: 1,
    transition: 'background-color 0.3s ease-in-out'
  },
  '& video': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 0
  },
  '& .video-icon': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 2,
    opacity: 0.4,
    transition: 'opacity 0.3s ease-in-out',
    pointerEvents: 'none'
  },
  '&:hover': {
    '& .play-button': {
      opacity: 1,
    },
    '& .video-icon': {
      opacity: 0.2,
    },
    '&::after': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)'
    }
  },
}));

/**
 * Tutorial Card Component with Video Thumbnail
 * Displays a tutorial card with a video thumbnail (3rd frame)
 */
const TutorialCard = ({ tutorial, onClick }) => {
  const videoRef = useRef(null);

  // Load video thumbnail (3rd frame) when component mounts
  useEffect(() => {
    if (!tutorial.videoSrc) return;

    const video = videoRef.current;
    if (!video) return;

    let isMounted = true;

    // Handler to seek to 3rd frame after metadata loads
    const handleSeekToThirdFrame = () => {
      if (!isMounted || !video) return;

      try {
        // Use a small time offset (0.3-1 second) to get the 3rd frame
        // This is efficient and doesn't trigger full video download
        const thumbnailTime = Math.min(1.0, video.duration > 0 ? Math.max(0.3, video.duration * 0.02) : 0.5);
        video.currentTime = thumbnailTime;
      } catch (error) {
        // If seeking fails, fallback to first frame
        if (video && isMounted) {
          video.currentTime = 0;
        }
      }
    };

    // Load thumbnail at a small time offset (3rd frame, ~0.3-1 seconds)
    const loadThumbnail = () => {
      try {
        // Load metadata first (lightweight - only downloads headers and first frame)
        if (video.readyState === 0) {
          video.load();
        }

        // If metadata is already loaded, seek immediately
        if (video.readyState >= 1 && video.duration) {
          const thumbnailTime = Math.min(1.0, Math.max(0.3, video.duration * 0.02));
          video.currentTime = thumbnailTime;
        } else {
          // Wait for metadata to load, then seek to 3rd frame
          video.addEventListener('loadedmetadata', handleSeekToThirdFrame, { once: true });
        }
      } catch (error) {
        // Fallback to first frame
        if (video && isMounted) {
          video.currentTime = 0;
        }
      }
    };

    // Wait a bit before loading thumbnail
    const timer = setTimeout(() => {
      if (video && isMounted) {
        loadThumbnail();
      }
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      if (video && handleSeekToThirdFrame) {
        video.removeEventListener('loadedmetadata', handleSeekToThirdFrame);
      }
    };
  }, [tutorial.videoSrc]);

  return (
    <StyledCard elevation={2}>
      <CardActionArea
        onClick={onClick}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <CardImageContainer>
          {tutorial.videoSrc && (
            <video
              ref={videoRef}
              src={tutorial.videoSrc}
              muted
              playsInline
              preload="metadata"
              style={{ 
                pointerEvents: 'none',
                objectFit: 'cover'
              }}
              aria-hidden="true"
            />
          )}
          <VideoIcon
            className="video-icon"
            sx={{
              fontSize: 64,
              color: 'primary.main',
              transition: 'opacity 0.3s ease-in-out',
            }}
          />
          <PlayButtonContainer className="play-button">
            <IconButton size="large">
              <PlayIcon />
            </IconButton>
          </PlayButtonContainer>
        </CardImageContainer>
        <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
            <PlayIcon color="primary" sx={{ fontSize: 20, mt: 0.5, flexShrink: 0 }} />
            <Typography variant="h6" component="h3" fontWeight={600} sx={{ lineHeight: 1.3 }}>
              {tutorial.title}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 1,
              lineHeight: 1.6,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {tutorial.description}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Chip
              label="Watch Video"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          </Box>
        </CardContent>
      </CardActionArea>
    </StyledCard>
  );
};

/**
 * Tutorials Page Component
 * Displays all available tutorials in a professional card-based grid layout
 */
const TutorialsPage = () => {
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const tutorials = getAllTutorials();

  const handleTutorialClick = (tutorial) => {
    setSelectedTutorial(tutorial);
    setShowPrompt(true);
  };

  const handlePromptClose = () => {
    setShowPrompt(false);
    setSelectedTutorial(null);
  };

  const handlePromptPlay = async () => {
    if (selectedTutorial) {
      // Mark tutorial as watched when user clicks Play (same as TutorialManager)
      await markTutorialWatched(selectedTutorial.featureId);
    }
    setShowPrompt(false);
    setShowVideoPlayer(true);
  };

  const handleVideoClose = () => {
    setShowVideoPlayer(false);
    setSelectedTutorial(null);
  };

  const handleVideoWatched = async () => {
    if (selectedTutorial) {
      // Mark tutorial as watched and save to database
      await markTutorialWatched(selectedTutorial.featureId);
    }
    // Don't close automatically - let user see next video button
  };

  const handleNextVideo = async (nextTutorial) => {
    if (nextTutorial) {
      // Mark current tutorial as watched if not already
      if (selectedTutorial) {
        await markTutorialWatched(selectedTutorial.featureId);
      }
      // Smoothly transition to next video without closing dialog
      // Just update the selected tutorial - the video player will handle the transition
      setSelectedTutorial(nextTutorial);
      // Keep video player open - it will update props smoothly
    }
  };

  // Get other tutorials for the prompt sidebar (excluding the selected one)
  const getOtherTutorials = () => {
    if (!selectedTutorial) return [];
    return tutorials
      .filter(t => t.featureId !== selectedTutorial.featureId)
      .map(tutorial => ({
        ...tutorial,
        onClick: () => {
          handlePromptClose();
          setSelectedTutorial(tutorial);
          setShowPrompt(true);
        }
      }));
  };

  return (
    <Container maxWidth="lg" sx={{ pt: { xs: 10, sm: 4 }, pb: 8 }}>
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
          Tutorial Videos
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Explore our comprehensive video guides to master all platform features. Click on any tutorial card to get started.
        </Typography>
      </Box>

      <Box>
        <Grid container spacing={3}>
          {tutorials.map((tutorial) => (
            <Grid item xs={12} sm={6} md={4} key={tutorial.featureId}>
              <TutorialCard
                tutorial={tutorial}
                onClick={() => handleTutorialClick(tutorial)}
              />
            </Grid>
          ))}
        </Grid>
        <Box sx={{ height: 10 }} />
      </Box>

      {/* Tutorial Prompt Dialog */}
      {showPrompt && selectedTutorial && (
        <TutorialPrompt
          open={showPrompt}
          onClose={handlePromptClose}
          featureName={selectedTutorial.title}
          description={selectedTutorial.description}
          videoSrc={selectedTutorial.videoSrc}
          onPlay={handlePromptPlay}
          onRemindLater={handlePromptClose}
          onDontShow={handlePromptClose}
          otherTutorials={getOtherTutorials()}
        />
      )}

      {/* Video Player */}
      {showVideoPlayer && selectedTutorial && (
        <TutorialVideoPlayer
          open={showVideoPlayer}
          onClose={handleVideoClose}
          videoSrc={selectedTutorial.videoSrc}
          title={selectedTutorial.title}
          transcript={selectedTutorial.transcript}
          onWatched={handleVideoWatched}
          featureId={selectedTutorial.featureId}
          onNextVideo={handleNextVideo}
        />
      )}
    </Container>
  );
};

export default TutorialsPage;

